import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ActivityForm from './ActivityForm';
import { formatDate, formatDateTime } from '../../utils/dateFormat';

const LeadDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [calendarEvents, setCalendarEvents] = useState([]);

  useEffect(() => {
    fetchLead();
  }, [id]);

  const fetchLead = async () => {
    try {
      const { data } = await axios.get(`/api/crm/leads/${id}`);
      setLead(data);
      await fetchCalendarEvents();
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Lead not found');
        navigate('/crm/leads');
      } else {
        toast.error('Failed to fetch lead');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarEvents = async () => {
    try {
      const { data } = await axios.get('/api/calendar/events', {
        params: { eventType: 'CRM' }
      });
      const leadEvents = data.filter(event => event.extendedProps?.referenceId === parseInt(id));
      setCalendarEvents(leadEvents);
    } catch (error) {
    }
  };

  const statusColors = {
    New: 'bg-blue-100 text-blue-800',
    Contacted: 'bg-yellow-100 text-yellow-800',
    Qualified: 'bg-green-100 text-green-800',
    Lost: 'bg-red-100 text-red-800',
    Converted: 'bg-purple-100 text-purple-800'
  };

  const activityTypeColors = {
    Call: 'bg-blue-50 border-blue-200',
    Meeting: 'bg-green-50 border-green-200',
    Email: 'bg-purple-50 border-purple-200',
    'Follow-up': 'bg-yellow-50 border-yellow-200'
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!lead) return <div className="p-6">Lead not found</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <button onClick={() => navigate('/crm/leads')} className="text-blue-600 hover:underline mb-2">
            ← Back to Leads
          </button>
          <h1 className="text-2xl font-bold">{lead.name}</h1>
        </div>
        <button
          onClick={() => navigate(`/crm/leads/${id}/edit`)}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Edit Lead
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <h2 className="text-lg font-semibold mb-4">Lead Information</h2>
            
            <div>
              <label className="text-sm text-gray-500">Status</label>
              <div className="mt-1">
                <span className={`px-3 py-1 rounded text-sm font-medium ${statusColors[lead.status]}`}>
                  {lead.status}
                </span>
              </div>
            </div>

            {lead.company && (
              <div>
                <label className="text-sm text-gray-500">Company</label>
                <div className="mt-1 font-medium">{lead.company}</div>
              </div>
            )}

            {lead.email && (
              <div>
                <label className="text-sm text-gray-500">Email</label>
                <div className="mt-1">
                  <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline">
                    {lead.email}
                  </a>
                </div>
              </div>
            )}

            {lead.phone && (
              <div>
                <label className="text-sm text-gray-500">Phone</label>
                <div className="mt-1">
                  <a href={`tel:${lead.phone}`} className="text-blue-600 hover:underline">
                    {lead.phone}
                  </a>
                </div>
              </div>
            )}

            <div>
              <label className="text-sm text-gray-500">Owner</label>
              <div className="mt-1 font-medium">{lead.owner?.username || '-'}</div>
            </div>

            {lead.nextFollowupAt && (
              <div>
                <label className="text-sm text-gray-500">Next Follow-up</label>
                <div className="mt-1 font-medium text-orange-600">
                  {formatDateTime(lead.nextFollowupAt)}
                </div>
              </div>
            )}

            {calendarEvents.length > 0 && (
              <div>
                <label className="text-sm text-gray-500">📅 Calendar Events</label>
                <div className="mt-2 space-y-2">
                  {calendarEvents.map((event) => (
                    <div key={event.id} className="text-sm bg-blue-50 p-2 rounded border border-blue-200">
                      <div className="font-medium text-blue-900">{event.title}</div>
                      <div className="text-xs text-blue-700">
                        {formatDateTime(event.start)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="text-sm text-gray-500">Created</label>
              <div className="mt-1">{formatDate(lead.createdAt)}</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Activities</h2>
              <button
                onClick={() => setShowActivityForm(!showActivityForm)}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                + New Activity
              </button>
            </div>

            {showActivityForm && (
              <div className="mb-6 p-4 bg-gray-50 rounded border">
                <ActivityForm
                  leadId={id}
                  onSuccess={() => {
                    setShowActivityForm(false);
                    fetchLead();
                  }}
                  onCancel={() => setShowActivityForm(false)}
                />
              </div>
            )}

            <div className="space-y-3">
              {lead.activities && lead.activities.length > 0 ? (
                lead.activities.map((activity) => (
                  <div
                    key={activity.id}
                    className={`p-4 rounded border-l-4 ${activityTypeColors[activity.activityType] || 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold">{activity.activityType}</span>
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            activity.status === 'Completed' ? 'bg-green-100 text-green-800' :
                            activity.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {activity.status}
                          </span>
                        </div>
                        {activity.description && (
                          <p className="text-sm text-gray-600 mb-2">{activity.description}</p>
                        )}
                        <div className="text-xs text-gray-500">
                          {activity.startTime && (
                            <span>📅 {formatDateTime(activity.startTime)}</span>
                          )}
                          {activity.creator && (
                            <span className="ml-3">👤 {activity.creator.username}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No activities yet. Create one to get started!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetails;
